import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
// Phase 2C.4 - `autoBreakStartHHMM` was used by the legacy
// "Break starts at" chip that has been removed from the day
// detail view. Import retained as a no-op reference in case the
// helper is needed by a future polish; ESLint-safe via the void.
import { autoBreakStartHHMM } from "@/lib/timeline";
import { resolveBankHolidayTheme } from "@/lib/bankHolidayTheme";
void autoBreakStartHHMM;
import { useAuth } from "@/context/AuthContext";
import { Card, Avatar } from "./Dashboard";
import { AssignmentTimeline } from "./RosterPage";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, CalendarDays, Lock, Users, MapPin } from "lucide-react";

const DAY_LABELS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

function iso(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function mondayOf(d) { const n = new Date(d); n.setDate(n.getDate() - ((n.getDay() + 6) % 7)); return n; }
function addDays(d, n) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }
function minToH(m) { return (Math.round((m / 60) * 10) / 10).toString().replace(/\.0$/, ""); }

/**
 * Read-only weekly roster for employees.
 * - Default view: "Me" - shows only the logged-in employee's row.
 * - Toggle to "Team" - shows everyone in the same team (still read-only).
 * - Click any day cell to open a popup with the full schedule, the assignment
 *   timeline, break info and any assignment notes from the admin.
 */
export default function MyRosterPage() {
  const { user } = useAuth();
  const [weekStart, setWeekStart] = useState(mondayOf(new Date()));
  const [scope, setScope] = useState("me");
  const [data, setData] = useState({ employees: [], shifts: [], allocations: [], leaves: [], bank_holidays: {}, period_status: "draft" });
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [view, setView] = useState(null);  // {employee, day, shift, alloc}
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: cats }, { data: locs }] = await Promise.all([
          api.get("/categories"),
          api.get("/locations").catch(() => ({ data: [] })),
        ]);
        setCategories(cats);
        setLocations(locs || []);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/roster?week_start=${iso(weekStart)}&scope=${scope}`);
        setData(data);
      } finally { setLoading(false); }
    })();
  }, [weekStart, scope]);

  const sm = useMemo(() => Object.fromEntries((data.shifts || []).map(s => [`${s.employee_id}_${s.work_date}`, s])), [data.shifts]);
  const am = useMemo(() => Object.fromEntries((data.allocations || []).map(a => [`${a.employee_id}_${a.work_date}`, a])), [data.allocations]);
  const locMap = useMemo(
    () => Object.fromEntries(locations.map((l) => [l.location_id, l])),
    [locations],
  );
  // Resolve a primary location for a cell, mirroring the manager view precedence:
  // shift.location_id → alloc.location_id → first entry with a location_id
  const resolveCellLocation = (sh, al) => {
    const id = sh?.location_id
      || al?.location_id
      || (al?.entries || []).find((e) => e?.location_id)?.location_id
      || null;
    return id ? locMap[id] : null;
  };
  const leaveByEmpDate = useMemo(() => {
    const out = {};
    for (const l of data.leaves || []) {
      const d0 = new Date(l.start_date), d1 = new Date(l.end_date);
      for (let d = new Date(d0); d <= d1; d.setDate(d.getDate() + 1)) {
        out[`${l.employee_id}_${iso(d)}`] = l;
      }
    }
    return out;
  }, [data.leaves]);

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const openCell = (emp, day) => {
    const key = `${emp.employee_id}_${iso(day)}`;
    setView({
      employee: emp,
      work_date: iso(day),
      day,
      shift: sm[key] || null,
      alloc: am[key] || null,
      leave: leaveByEmpDate[key] || null,
      is_bh: (data.bank_holidays[emp.employee_id] || []).includes(iso(day)),
    });
  };

  // TP-RS.team.2 Phase 3 hotfix #4 -- partial-publish wording. After
  // the backend partial-publish visibility fix, an employee whose
  // team is in `published_team_ids` already sees their own rows even
  // while `period_status` remains "draft" (the period only flips to
  // "published" when EVERY team with shifts is published). Before
  // this fix the pill said "Draft" + the misleading "manager hasn't
  // published this week yet" banner. We now treat the employee's
  // team being inside `published_team_ids` as an effective publish
  // for that employee.
  const myTeamId = (data.employees && data.employees[0])?.team_id || null;
  const myTeamIsPartialPublished = Boolean(
    myTeamId && Array.isArray(data.published_team_ids)
    && data.published_team_ids.includes(myTeamId));
  const effectiveStatus =
    data.period_status === "locked" ? "locked"
    : data.period_status === "published" ? "published"
    : (myTeamIsPartialPublished ? "team_published" : "draft");

  const periodLabel =
    effectiveStatus === "locked" ? "Locked"
    : effectiveStatus === "published" ? "Published"
    : effectiveStatus === "team_published" ? "Published"
    : "Draft";
  const periodTone =
    effectiveStatus === "locked" ? "bg-slate-100 text-slate-700"
    : effectiveStatus === "published" ? "bg-[#DBE7FE] text-[#1D4ED8]"
    : effectiveStatus === "team_published" ? "bg-[#DBE7FE] text-[#1D4ED8]"
    : "bg-amber-100 text-amber-800";

  return (
    <div>
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="eyebrow">My roster</div>
          <h1 className="font-display text-4xl mt-1">Week of {iso(weekStart)}</h1>
          <p className="text-sm text-[#4B5773] mt-2">
            View your published shifts and your team&apos;s schedule. Daily details are view-only, speak to your manager to request changes.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
            <button onClick={()=>setScope("me")} data-testid="scope-me"
              className={`text-xs px-3 py-1.5 rounded-md ${scope === "me" ? "bg-[#2563EB] text-white" : "text-[#4B5773] hover:bg-slate-50"}`}>
              <Users className="inline h-3 w-3 -mt-0.5 mr-1"/>My week
            </button>
            <button onClick={()=>setScope("team")} data-testid="scope-team"
              className={`text-xs px-3 py-1.5 rounded-md ${scope === "team" ? "bg-[#2563EB] text-white" : "text-[#4B5773] hover:bg-slate-50"}`}>
              Team roster
            </button>
          </div>
          <Button variant="outline" size="sm" data-testid="myroster-prev"
            onClick={()=>setWeekStart(addDays(weekStart, -7))}><ChevronLeft className="h-4 w-4"/></Button>
          <Button variant="outline" size="sm" onClick={()=>setWeekStart(mondayOf(new Date()))}>Today</Button>
          <Button variant="outline" size="sm" data-testid="myroster-next"
            onClick={()=>setWeekStart(addDays(weekStart, 7))}><ChevronRight className="h-4 w-4"/></Button>
        </div>
      </div>

      <div className="mb-4 flex items-center gap-2 text-xs">
        <span className={`px-2.5 py-1 rounded-full ${periodTone}`} data-testid="period-status-pill">{periodLabel}</span>
        {effectiveStatus === "team_published" && (
          <span className="text-[#4B5773]" data-testid="period-status-banner">Your team rota has been published.</span>
        )}
        {effectiveStatus === "draft" && (
          <span className="text-[#4B5773]" data-testid="period-status-banner">Your manager hasn&apos;t published this week yet - check back later.</span>
        )}
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px] sm:min-w-0" data-testid="myroster-grid">
            <thead>
              <tr className="text-left text-[#4B5773] border-b border-slate-200">
                <th className="py-3 px-2 sm:pr-3 eyebrow font-normal sticky left-0 bg-white z-10 w-[38vw] sm:w-auto min-w-[110px] sm:min-w-[160px] max-w-[55vw] shadow-[2px_0_0_0_rgba(0,0,0,0.04)]">Person</th>
                {days.map((d, i) => (
                  <th key={i} className="py-3 px-2 eyebrow font-normal min-w-[80px] sm:min-w-[110px]">
                    <div>{DAY_LABELS[i]}</div>
                    <div className="text-[10px] tracking-normal text-slate-400">{d.getDate()}/{d.getMonth() + 1}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.employees.length === 0 && !loading && (
                <tr><td colSpan={8} className="py-12 text-center text-[#4B5773]" data-testid="myroster-empty">
                  Nothing to show yet for this week.
                </td></tr>
              )}
              {data.employees.map(emp => (
                <tr key={emp.employee_id} data-testid={`myroster-row-${emp.employee_id}`}>
                  <td className="py-3 px-2 sm:pr-3 sticky left-0 bg-white z-10 w-[38vw] sm:w-auto max-w-[55vw] shadow-[2px_0_0_0_rgba(0,0,0,0.04)]">
                    <div className="flex items-center gap-2 min-w-0">
                      <Avatar name={emp.name}/>
                      <div className="text-sm font-medium text-[#0B1A36] truncate">{emp.name}</div>
                    </div>
                  </td>
                  {days.map((d, i) => {
                    const key = `${emp.employee_id}_${iso(d)}`;
                    const sh = sm[key]; const al = am[key];
                    const leave = leaveByEmpDate[key];
                    const isBh = (data.bank_holidays[emp.employee_id] || []).includes(iso(d));
                    const bhName = (data.bank_holiday_names?.[emp.employee_id] || {})[iso(d)] || null;
                    const bhTheme = isBh ? resolveBankHolidayTheme(bhName) : null;
                    const BhIcon = bhTheme?.Icon;
                    return (
                      <td key={i} className="px-1 py-1 align-top">
                        <button type="button"
                          onClick={()=>openCell(emp, d)}
                          data-testid={`myroster-cell-${emp.employee_id}-${iso(d)}`}
                          className="w-full text-left p-2 rounded-lg border border-slate-200 hover:border-[#2563EB] hover:bg-[#F6F9FE] min-h-[64px] transition-colors">
                          {leave ? (
                            <div className="text-xs">
                              <div className="font-medium text-[#7C3AED]">{leave.category_name || "Leave"}</div>
                              <div className="text-[10px] text-[#4B5773]">Approved</div>
                            </div>
                          ) : isBh ? (
                            <div className={`text-xs rounded-md border ring-1 px-2 py-1 inline-flex items-center gap-1.5 ${bhTheme.className}`}
                                 data-bh-name={bhName || undefined}
                                 data-bh-theme={bhTheme.key}
                                 title={bhName || "Bank holiday"}>
                              <BhIcon className="h-3 w-3 shrink-0" aria-hidden="true" />
                              <span className="font-medium truncate">{bhName || "Bank holiday"}</span>
                            </div>
                          ) : sh ? (
                            <div className="text-xs">
                              <div className="font-medium">{sh.start_time}–{sh.end_time}</div>
                              {(() => {
                                const loc = resolveCellLocation(sh, al);
                                if (!loc) return null;
                                return (
                                  <div className="mt-1 flex items-center gap-1 min-w-0"
                                    data-testid={`myroster-cell-location-${emp.employee_id}-${iso(d)}`}
                                    title={loc.address || loc.name}>
                                    <span className="inline-block h-1.5 w-1.5 rounded-full shrink-0"
                                      style={{ backgroundColor: loc.colour || "#64748B" }} />
                                    <MapPin className="h-2.5 w-2.5 text-[#4B5773] shrink-0" />
                                    <span className="text-[10px] text-[#4B5773] truncate">{loc.name}</span>
                                  </div>
                                );
                              })()}
                              {(al?.entries?.length || 0) > 0 && (() => {
                                const total = al.entries.reduce((s, e) => s + (e.minutes || 0), 0) || 1;
                                return (
                                  <div className="mt-1 flex h-1 w-full rounded-full overflow-hidden bg-slate-100"
                                    data-testid={`myroster-cell-bar-${emp.employee_id}-${iso(d)}`}>
                                    {al.entries.map((e, ix) => {
                                      const cat = categories.find((c) => c.category_id === e.category_id);
                                      return (
                                        <div key={ix}
                                          title={`${e.category_name || ""} ${minToH(e.minutes || 0)}h`}
                                          style={{
                                            width: `${((e.minutes || 0) / total) * 100}%`,
                                            backgroundColor: cat?.color || "#94a3b8",
                                          }} />
                                      );
                                    })}
                                  </div>
                                );
                              })()}
                              <div className="mt-0.5 text-[10px] text-[#4B5773] truncate">
                                {al?.entries?.length === 1
                                  ? (al.entries[0].category_name || `${minToH(sh.total_minutes || 0)}h`)
                                  : al?.entries?.length
                                  ? `${al.entries.length} tasks`
                                  : `${minToH(sh.total_minutes || 0)}h`}
                              </div>
                            </div>
                          ) : (
                            <div className="text-[10px] text-slate-300">-</div>
                          )}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Read-only day details */}
      <Dialog open={!!view} onOpenChange={()=>setView(null)}>
        <DialogContent className="max-w-xl" fullscreenOnMobile>
          {view && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Lock className="h-4 w-4 text-[#4B5773]"/>
                  {view.employee.name} · {DAY_LABELS[(view.day.getDay() + 6) % 7]} {view.day.getDate()}/{view.day.getMonth() + 1}
                </DialogTitle>
              </DialogHeader>
              {view.leave ? (
                <div className="rounded-lg bg-[#F6F9FE] border border-slate-200 p-4 text-sm">
                  <div className="font-medium text-[#7C3AED] mb-1">{view.leave.category_name}</div>
                  <div className="text-[#4B5773]">{view.leave.start_date} → {view.leave.end_date}</div>
                  {view.leave.notes && <div className="mt-2 text-[#1F2A44]">{view.leave.notes}</div>}
                </div>
              ) : view.is_bh ? (
                <div className="rounded-lg bg-[#F6F9FE] border border-slate-200 p-4 text-sm">
                  <div className="font-medium text-[#0EA5E9]">Bank holiday</div>
                </div>
              ) : view.shift ? (
                <div className="space-y-4 text-sm" data-testid="day-detail">
                  {/* Phase 2C.4 - the legacy Break stat and "Break
                      starts at" chip have been removed from the
                      day view. Break is now a normal Away
                      assignment row rendered by the
                      AssignmentTimeline below (which is the single
                      visual source of truth). When an Away entry
                      exists we also pass `break_minutes: 0` /
                      `break_start_time: null` to the timeline so
                      the shadow shift field cannot visually
                      synthesise a parallel break row. */}
                  {(() => {
                    const _p2c4HasAway = (view.alloc?.entries || []).some((e) => {
                      const cat = categories.find((c) => c.category_id === e.category_id);
                      return cat && cat.kind === "away";
                    });
                    return (
                      <>
                        <div className="grid grid-cols-2 gap-3">
                          <Stat label="Start" value={view.shift.start_time}/>
                          <Stat label="End" value={view.shift.end_time}/>
                        </div>
                        {(() => {
                          const loc = resolveCellLocation(view.shift, view.alloc);
                          if (!loc) return null;
                          return (
                            <div className="flex items-center gap-2 text-xs text-[#4B5773]"
                              data-testid="day-detail-location">
                              <MapPin className="h-3.5 w-3.5"/>
                              <span>Location:</span>
                              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white border border-slate-200 text-[#1F2A44]">
                                <span className="inline-block h-1.5 w-1.5 rounded-full"
                                  style={{ backgroundColor: loc.colour || "#64748B" }} />
                                <span className="font-medium">{loc.name}</span>
                              </span>
                              {loc.address && <span className="text-[10px] truncate">· {loc.address}</span>}
                            </div>
                          );
                        })()}
                        <AssignmentTimeline
                          shift={{
                            start_time: view.shift.start_time, end_time: view.shift.end_time,
                            break_minutes: _p2c4HasAway ? 0 : view.shift.break_minutes,
                            break_start_time: _p2c4HasAway ? null : view.shift.break_start_time,
                            entries: view.alloc?.entries || [],
                          }}
                          categories={categories} />
                      </>
                    );
                  })()}
                  {view.alloc?.entries?.some(e => e.note) && (
                    <div data-testid="day-notes">
                      <div className="eyebrow mb-1">Notes from your manager</div>
                      <ul className="space-y-1">
                        {view.alloc.entries.filter(e => e.note).map((e, i) => (
                          <li key={i} className="text-xs text-[#1F2A44]">
                            <span className="font-medium">{e.category_name}:</span> {e.note}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-[#4B5773]">No shift scheduled this day.</div>
              )}
              <div className="text-[10px] uppercase tracking-widest text-slate-400 text-center pt-2 border-t border-slate-100">Read-only view</div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg bg-[#F6F9FE] border border-slate-200 p-3">
      <div className="eyebrow text-[10px]">{label}</div>
      <div className="text-base font-medium text-[#0B1A36] mt-0.5">{value}</div>
    </div>
  );
}
